import { useState, type ReactElement } from 'react'
import type { ConceptEntity, EntityRole } from '@/types/knowledge_graph'
import { PencilIcon } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Card } from '@/components/ui/card'
import { InferenceTypeBadge } from './InferenceTypeBadge'

const FALLBACK_ENTITY_ROLE_LABELS: Record<EntityRole, string> = {
  actor: 'アクター',
  resource: 'リソース',
  event: 'イベント',
  unknown: '未分類'
}

function entityRoleLabel (role: EntityRole, labels: Partial<Record<EntityRole, string>>): string {
  return labels[role] ?? FALLBACK_ENTITY_ROLE_LABELS[role]
}

function entityRoleTagClass (role: EntityRole): string {
  if (role === 'actor') return 'text-(--bp-ok)'
  if (role === 'resource') return 'text-(--bp-accent)'
  if (role === 'event') return 'text-(--bp-warning)'
  return 'text-(--bp-ink3)'
}

export function EntityCard ({
  entity,
  onUpdate,
  roleLabels = {}
}: {
  entity: ConceptEntity
  onUpdate: (changes: Partial<ConceptEntity>) => void
  roleLabels?: Partial<Record<EntityRole, string>>
}): ReactElement {
  const [editing, setEditing] = useState(false)
  const [draftName, setDraftName] = useState('')
  const [draftDescription, setDraftDescription] = useState('')
  const [draftRole, setDraftRole] = useState<EntityRole>('resource')
  const [showAttributes, setShowAttributes] = useState(false)

  function startEdit (): void {
    setDraftName(entity.name)
    setDraftDescription(entity.description ?? '')
    setDraftRole(entity.entity_role)
    setEditing(true)
  }

  function commitEdit (): void {
    const trimmedName = draftName.trim()
    if (trimmedName === '') return
    const changes: Partial<ConceptEntity> = {}
    if (trimmedName !== entity.name) changes.name = trimmedName
    const trimmedDesc = draftDescription.trim()
    if (trimmedDesc !== (entity.description ?? '')) changes.description = trimmedDesc
    if (draftRole !== entity.entity_role) changes.entity_role = draftRole
    if (Object.keys(changes).length > 0) onUpdate(changes)
    setEditing(false)
  }

  function cancelEdit (): void {
    setEditing(false)
  }

  if (editing) {
    const isEvent = draftRole === 'event'
    return (
      <Card
        className={cn(
          'relative border-[1.5px] rounded-lg shadow-none p-3.5 flex flex-col gap-1.5',
          isEvent && 'border-(--bp-warning-border) bg-(--bp-warning-pale)'
        )}
      >
        <select
          value={draftRole}
          onChange={(e) => setDraftRole(e.target.value as EntityRole)}
          className={cn(
            'self-start text-[10px] font-bold border border-(--bp-border) rounded px-1 py-0.5 bg-(--bp-surface) cursor-pointer',
            isEvent ? 'text-(--bp-warning)' : 'text-(--bp-accent)'
          )}
        >
          <option value='actor'>{entityRoleLabel('actor', roleLabels)}</option>
          <option value='resource'>{entityRoleLabel('resource', roleLabels)}</option>
          <option value='event'>{entityRoleLabel('event', roleLabels)}</option>
          <option value='unknown'>{entityRoleLabel('unknown', roleLabels)}</option>
        </select>
        <input
          autoFocus
          value={draftName}
          onChange={(e) => setDraftName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); commitEdit() }
            if (e.key === 'Escape') cancelEdit()
          }}
          placeholder='エンティティ名'
          className='w-full text-sm font-semibold border border-(--bp-accent) rounded px-1.5 py-0.75 bg-(--bp-surface) text-(--bp-ink) outline-none'
        />
        <textarea
          value={draftDescription}
          onChange={(e) => setDraftDescription(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') cancelEdit() }}
          placeholder='説明（省略可）'
          rows={2}
          className='w-full resize-y text-[11px] border border-(--bp-border) rounded px-1.5 py-0.75 bg-(--bp-surface) text-(--bp-ink2) outline-none'
        />
        <div className='flex gap-1 justify-end'>
          <button
            onClick={cancelEdit}
            className='text-[11px] px-2 py-0.5 border border-(--bp-border) rounded bg-(--bp-surface2) text-(--bp-ink2) cursor-pointer'
          >
            キャンセル
          </button>
          <button
            onClick={commitEdit}
            className='text-[11px] px-2 py-0.5 border-none rounded bg-(--bp-accent) text-white cursor-pointer'
          >
            保存
          </button>
        </div>
      </Card>
    )
  }

  const isEvent = entity.entity_role === 'event'
  return (
    <Card
      className={cn(
        'relative border-[1.5px] rounded-lg shadow-none p-3.5 pt-3',
        isEvent && 'border-(--bp-warning-border) bg-(--bp-warning-pale)'
      )}
    >
      <div className='flex items-center justify-between gap-1'>
        <div className={cn('text-[10px] font-bold tracking-[.06em] uppercase mb-1.25', entityRoleTagClass(entity.entity_role))}>
          {entityRoleLabel(entity.entity_role, roleLabels)}
        </div>
        {entity.source === 'implicit' && (
          <InferenceTypeBadge
            tag={{
              icon: '💭',
              label: '推論',
              style: {
                background: 'var(--bp-accent-pale)',
                color: 'var(--bp-ai)',
                marginRight: 24,
                flexShrink: 0
              }
            }}
            reasoning={entity.reasoning}
          />
        )}
      </div>
      <div className='text-sm font-bold text-(--bp-ink) tracking-tight mb-1.25'>{entity.name}</div>
      {entity.description != null && entity.description !== '' && (
        <div className='text-[11px] text-(--bp-ink3) leading-relaxed'>{entity.description}</div>
      )}
      {(entity.attributes != null) && entity.attributes.length > 0 && (
        <div className='mt-1.5'>
          <button
            type='button'
            className='text-[10px] font-semibold text-(--bp-accent) bg-none border-none p-0 cursor-pointer hover:underline'
            onClick={() => setShowAttributes((v) => !v)}
          >
            {showAttributes ? '属性を隠す' : `属性を表示（${entity.attributes.length}件）`}
          </button>
          {showAttributes && (
            <ul className='list-none mt-1.5 px-2 py-1.5 bg-(--bp-surface2) rounded text-[11px] text-(--bp-ink3) leading-relaxed'>
              {entity.attributes.map((attr) => (
                <li key={attr.name}>
                  <span className='font-semibold text-(--bp-ink2)'>{attr.name}</span>
                  {attr.description != null && attr.description !== '' && <span>：{attr.description}</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      <button
        className='absolute top-2 right-2 size-6 flex items-center justify-center bg-(--bp-surface2) border border-(--bp-border) rounded-[5px] cursor-pointer text-(--bp-ink3) p-0 transition-colors hover:bg-(--bp-accent-pale) hover:text-(--bp-accent) hover:border-[rgba(53,71,193,.3)]'
        title='名前を修正'
        onClick={startEdit}
      >
        <PencilIcon width={12} height={12} />
      </button>
    </Card>
  )
}
